import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import { fetchShops, fetchShop } from '@/requests/shop'
import { getUser } from '@/requests/users'
import { ChevronsUpDown, Plus } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useShop } from '@/context/shop-context'
import { NewShopDrawer } from './new-shop-drawer'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'

type SwitcherShop = {
  id: string
  _id: string
  name: {
    en: string
    tr: string
  }
  logo_file: {
    url: string
  }
  plan: string
  slug: string
}

export function ShopSwitcher({}: {}) {
  const { isMobile } = useSidebar()
  const { activeShop, setActiveShop } = useShop()
  const { setUser } = useAuthStore.getState().auth
  const [isNewShopDrawerOpen, setIsNewShopDrawerOpen] = React.useState(false)
  const search = useSearch({ from: '/_authenticated' })

  const { data } = useQuery({ queryKey: ['shops.list'], queryFn: fetchShops() })

  React.useEffect(() => {
    if (search?.newShop === '1') {
      setIsNewShopDrawerOpen(true)
    }
  }, [search?.newShop])

  const getShopName = (shop: SwitcherShop | any) => {
    if (!shop || !shop.name) return ''
    const lang = localStorage.getItem('menu-ui-language') || 'en'
    return (
      shop.name[lang] ||
      shop.name.en ||
      shop.name.tr ||
      Object.values(shop.name)[0] ||
      ''
    )
  }

  React.useEffect(() => {
    if (data && data?.[0]) {
      const lastSelectedShop = localStorage.getItem('lastSelectedShop')
        ? JSON.parse(localStorage.getItem('lastSelectedShop') as string)
        : null

      // Check if the last selected shop exists in the data
      // We trust lastSelectedShop if it exists to handle race conditions (e.g. just created shop not in list yet)
      const isLastSelectedShopValid = !!lastSelectedShop

      const uiLanguage = localStorage.getItem('menu-ui-language') || 'en'

      const baseShop: SwitcherShop = isLastSelectedShopValid
        ? { ...lastSelectedShop, ui_language: uiLanguage }
        : {
            logo_file: {
              url: data[0].logo_file?.url,
            },
            ...data[0],
            ui_language: uiLanguage,
          }

      fetchShop(baseShop._id)
        .then((detail) => {
          const settings = detail.settings || {}
          const shopData = {
            ...baseShop,
            ...detail,
            settings,
            ui_language: uiLanguage,
          }
          setActiveShop(shopData as unknown as any)
          localStorage.setItem('lastSelectedShop', JSON.stringify(shopData))
        })
        .catch(() => {
          // If fetch fails (e.g. shop deleted), fallback to first in list
          if (data[0]) {
             const fallbackShop = { ...data[0], ui_language: uiLanguage }
             setActiveShop(fallbackShop as any)
             localStorage.setItem('lastSelectedShop', JSON.stringify(fallbackShop))
          }
        })
    } else if (data && data?.length === 0) {
      getUser(useAuthStore.getState().auth.getUser()?.id || '').then((user) => {
        setUser(user)
        window.location.href = `/shop-sign-up?step=${user.organization_id ? '2' : '1'}`
      })
    }
  }, [data])

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {activeShop && (
              <SidebarMenuButton
                size='lg'
                className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
              >
                <div className='flex aspect-square size-8 items-center justify-center rounded-lg text-sidebar-primary-foreground'>
                  <img src={activeShop.logo_file?.url} className='size-4' />
                </div>
                <div className='grid flex-1 text-left text-sm leading-tight'>
                  <span className='truncate font-semibold'>
                    {getShopName(activeShop)}
                  </span>
                  <span className='truncate text-xs'>{activeShop.plan}</span>
                </div>
                <ChevronsUpDown className='ml-auto' />
              </SidebarMenuButton>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg'
            align='start'
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            <DropdownMenuLabel className='text-xs text-muted-foreground'>
              Teams
            </DropdownMenuLabel>
            <div className='max-h-[300px] overflow-y-auto'>
              {data &&
                data.map((shop: SwitcherShop, index: number) => (
                  <DropdownMenuItem
                    key={shop._id ?? shop.id}
                    onClick={() => {
                      const uiLanguage =
                        localStorage.getItem('menu-ui-language') || 'tr'
                      fetchShop(shop._id).then((detail) => {
                        const settings = detail.settings || {}
                        const shopSettings = {
                          ...shop,
                          ...detail,
                          settings,
                          ui_language: uiLanguage as 'tr' | 'en',
                        }
                        setActiveShop(shopSettings as unknown as any)
                        localStorage.setItem(
                          'lastSelectedShop',
                          JSON.stringify(shopSettings)
                        )
                      })
                    }}
                    className='gap-2 p-2'
                  >
                    <div className='flex size-6 items-center justify-center rounded-sm border'>
                      <img
                        src={shop.logo_file?.url}
                        className='size-4 shrink-0'
                      />
                    </div>
                    {getShopName(shop)}
                    <DropdownMenuShortcut>⌘{index + 1}</DropdownMenuShortcut>
                  </DropdownMenuItem>
                ))}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className='gap-2 p-2 cursor-pointer'
              onClick={() => setIsNewShopDrawerOpen(true)}
            >
              <div className='flex size-6 items-center justify-center rounded-md border bg-background'>
                <Plus className='size-4' />
              </div>
              <div className='font-medium text-muted-foreground'>
                Add new shop
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>

      {/* New Shop Drawer */}
      <NewShopDrawer
        open={isNewShopDrawerOpen}
        onOpenChange={setIsNewShopDrawerOpen}
      />
    </SidebarMenu>
  )
}
